import { useCallback, useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import { API_ENDPOINTS } from '../../config/api'
import {
  updateUserStandardGrants,
  updateRestrictedAccess,
  fetchEffectiveStandardAccess,
  type StandardGrantRow,
  type EnrichedStandardAccessItem,
} from '../../services/standardAccessApi'
function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}` } : {}
}

type CatalogItem = { code: string; name?: string }

type Props = {
  userId: string
  organizationId: string
}

export default function UserStandardAccessPanel({ userId, organizationId }: Props) {
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [catalog, setCatalog] = useState<CatalogItem[]>([])
  const [orgGrants, setOrgGrants] = useState<StandardGrantRow[]>([])
  const [userEnabled, setUserEnabled] = useState<Record<string, boolean>>({})
  const [grantOrgToo, setGrantOrgToo] = useState<Record<string, boolean>>({})
  const [restricted, setRestricted] = useState(false)
  const [restrictedCodes, setRestrictedCodes] = useState<Record<string, boolean>>({})
  const [effectiveSummary, setEffectiveSummary] = useState<EnrichedStandardAccessItem[]>([])

  const load = useCallback(async () => {
    if (!userId || !organizationId) return
    setLoading(true)
    setError(null)
    try {
      const [grantsRes, restrictedRes, orgRes] = await Promise.all([
        fetch(API_ENDPOINTS.ADMIN_USER_STANDARD_GRANTS(userId, organizationId), {
          headers: getAuthHeaders(),
        }),
        fetch(API_ENDPOINTS.ADMIN_USER_RESTRICTED_ACCESS(userId, organizationId), {
          headers: getAuthHeaders(),
        }),
        fetch(API_ENDPOINTS.ADMIN_ORG_STANDARD_GRANTS(organizationId), {
          headers: getAuthHeaders(),
        }),
      ])
      const grantsBody = await grantsRes.json()
      const restrictedBody = await restrictedRes.json()
      const orgBody = await orgRes.json()
      if (!grantsRes.ok) throw new Error(grantsBody?.message || 'Failed to load user grants')
      const gData = grantsBody.data || grantsBody
      const userRows: StandardGrantRow[] = gData.grants || []
      const orgRows: StandardGrantRow[] = gData.organizationGrants || orgBody.data?.grants || []
      const cat: CatalogItem[] = (orgBody.data?.catalog || []).map((c: { code: string; name?: string }) => ({
        code: c.code,
        name: c.name,
      }))
      setCatalog(cat)
      setOrgGrants(orgRows)
      const uMap: Record<string, boolean> = {}
      const orgMap: Record<string, boolean> = {}
      userRows.forEach((r) => {
        uMap[r.standardCode] = r.enabled !== false
        if ((r as { grantOrgToo?: boolean }).grantOrgToo) orgMap[r.standardCode] = true
      })
      setUserEnabled(uMap)
      setGrantOrgToo(orgMap)

      const rData = restrictedBody.data || restrictedBody
      const profile = rData.profile || rData
      setRestricted(!!profile?.enabled)
      const codes: string[] =
        profile?.standardCodes ||
        (profile?.scopes || []).map((s: { standardCode: string }) => s.standardCode) ||
        []
      const rc: Record<string, boolean> = {}
      codes.forEach((c: string) => {
        rc[c] = true
      })
      setRestrictedCodes(rc)

      try {
        const effective = await fetchEffectiveStandardAccess(organizationId, userId)
        setEffectiveSummary(effective.standards || [])
      } catch {
        setEffectiveSummary([])
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Load failed')
    } finally {
      setLoading(false)
    }
  }, [userId, organizationId])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo(() => {
    const codes = new Set([
      ...catalog.map((c) => c.code),
      ...orgGrants.map((g) => g.standardCode),
      ...Object.keys(userEnabled),
    ])
    return Array.from(codes).sort((a, b) => a.localeCompare(b))
  }, [catalog, orgGrants, userEnabled])

  const orgGranted = useMemo(() => {
    const s = new Set<string>()
    orgGrants.filter((g) => g.enabled !== false).forEach((g) => s.add(g.standardCode))
    return s
  }, [orgGrants])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const standards = Object.entries(userEnabled)
        .filter(([, on]) => on)
        .map(([standardCode]) => ({
          standardCode,
          enabled: true,
          grantOrgToo: !!grantOrgToo[standardCode],
        }))
      await updateUserStandardGrants(userId, organizationId, standards)
      await updateRestrictedAccess(userId, organizationId, {
        enabled: restricted,
        standardCodes: Object.entries(restrictedCodes)
          .filter(([, on]) => on)
          .map(([code]) => code),
        mode: 'STANDARD_SCOPE',
      })
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!userId || !organizationId) return null

  return (
    <section className="mt-4 border-t pt-4">
      <h4 className="text-sm font-semibold mb-1">
        {t('standardGovernance.userGrantsTitle', 'Standard access for user')}
      </h4>
      {effectiveSummary.length > 0 ? (
        <div className="text-xs mb-3 p-2 bg-blue-50 rounded border border-blue-100">
          <p className="font-medium mb-1">{t('standardGovernance.effectiveAccess', 'Effective access')}</p>
          <ul className="space-y-1">
            {effectiveSummary.map((s) => (
              <li key={s.standardCode}>
                {s.displayName || s.standardCode}:{' '}
                {s.grantSources?.includes('ORGANIZATION')
                  ? t('standardGovernance.grantedByOrganization', 'Granted by organization')
                  : ''}
                {s.grantSources?.includes('ORGANIZATION') && s.grantSources?.includes('USER')
                  ? ' + '
                  : ''}
                {s.grantSources?.includes('USER')
                  ? t('standardGovernance.grantedDirectlyToUser', 'Granted directly to user')
                  : ''}
                {s.active ? ` · ${t('standardGovernance.activeStandard', 'Active')}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {restricted ? (
        <p className="text-xs text-amber-800 mb-2">
          {t('standardGovernance.restrictedToSelectedStandards', 'Restricted to selected standards')}:{' '}
          {Object.entries(restrictedCodes)
            .filter(([, on]) => on)
            .map(([c]) => c)
            .join(', ') || '—'}
        </p>
      ) : null}
      {error ? <p className="text-xs text-red-600 mb-2">{error}</p> : null}
      {loading ? (
        <p className="text-xs text-gray-500">{t('common.loading', 'Loading…')}</p>
      ) : (
        <>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input
              type="checkbox"
              checked={restricted}
              onChange={(e) => setRestricted(e.target.checked)}
            />
            {t(
              'standardGovernance.restrictedToggle',
              'Restrict this user to selected standards and their scopes',
            )}
          </label>
          {restricted ? (
            <p className="text-xs text-amber-700 mb-2">
              {t(
                'standardGovernance.restrictedHint',
                'Visibility follows standard applicability on information systems (read-only resolver in phase 1).',
              )}
            </p>
          ) : null}
          <ul className="max-h-40 overflow-y-auto text-sm space-y-2 mb-3">
            {rows.map((code) => {
              const fromOrg = orgGranted.has(code)
              return (
                <li key={code} className="flex flex-col gap-1 border-b border-gray-100 pb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{catalog.find((c) => c.code === code)?.name || code}</span>
                    {fromOrg ? (
                      <span className="text-xs text-green-700">
                        {t('standardGovernance.grantedByOrganization', 'Granted by organization')}
                      </span>
                    ) : null}
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!userEnabled[code]}
                      onChange={(e) =>
                        setUserEnabled((prev) => ({ ...prev, [code]: e.target.checked }))
                      }
                    />
                    {t('standardGovernance.grantedToUser', 'Granted to user')}
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!!grantOrgToo[code]}
                      disabled={!userEnabled[code]}
                      onChange={(e) =>
                        setGrantOrgToo((prev) => ({ ...prev, [code]: e.target.checked }))
                      }
                    />
                    {t('standardGovernance.alsoGrantToOrganization', 'Also grant this standard to the whole organization')}
                  </label>
                  {restricted ? (
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!restrictedCodes[code]}
                        onChange={(e) =>
                          setRestrictedCodes((prev) => ({ ...prev, [code]: e.target.checked }))
                        }
                      />
                      {t('standardGovernance.restrictedStandard', 'Include in restricted scope')}
                    </label>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </>
      )}
      <Button type="button" size="small" color="primary" disabled={saving || loading} onClick={() => void handleSave()}>
        {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
      </Button>
    </section>
  )
}
